import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from './ui/Modal';
import Input from './ui/Input';
import Button from './ui/Button';
import Icon from './AppIcon';

// Estados do Brasil (sigla e nome)
const ESTADOS_BRASIL = [
  { sigla: 'AC', nome: 'Acre' },
  { sigla: 'AL', nome: 'Alagoas' },
  { sigla: 'AP', nome: 'Amapá' },
  { sigla: 'AM', nome: 'Amazonas' },
  { sigla: 'BA', nome: 'Bahia' },
  { sigla: 'CE', nome: 'Ceará' },
  { sigla: 'DF', nome: 'Distrito Federal' },
  { sigla: 'ES', nome: 'Espírito Santo' },
  { sigla: 'GO', nome: 'Goiás' },
  { sigla: 'MA', nome: 'Maranhão' },
  { sigla: 'MT', nome: 'Mato Grosso' },
  { sigla: 'MS', nome: 'Mato Grosso do Sul' },
  { sigla: 'MG', nome: 'Minas Gerais' },
  { sigla: 'PA', nome: 'Pará' },
  { sigla: 'PB', nome: 'Paraíba' },
  { sigla: 'PR', nome: 'Paraná' },
  { sigla: 'PE', nome: 'Pernambuco' },
  { sigla: 'PI', nome: 'Piauí' },
  { sigla: 'RJ', nome: 'Rio de Janeiro' },
  { sigla: 'RN', nome: 'Rio Grande do Norte' },
  { sigla: 'RS', nome: 'Rio Grande do Sul' },
  { sigla: 'RO', nome: 'Rondônia' },
  { sigla: 'RR', nome: 'Roraima' },
  { sigla: 'SC', nome: 'Santa Catarina' },
  { sigla: 'SP', nome: 'São Paulo' },
  { sigla: 'SE', nome: 'Sergipe' },
  { sigla: 'TO', nome: 'Tocantins' }
];

// Regiões/cidades do Distrito Federal
const CIDADES_DISTRITO_FEDERAL = [
  'Asa Norte',
  'Asa Sul',
  'Águas Claras',
  'Taguatinga',
  'Ceilândia',
  'Gama',
  'Guará',
  'Planaltina',
  'Sobradinho',
  'Brazlândia',
  'Santa Maria',
  'São Sebastião',
  'Recanto das Emas',
  'Samambaia',
  'Paranoá',
  'Itapoã',
  'Varjão',
  'Park Way',
  'Riacho Fundo',
  'Riacho Fundo II',
  'Candangolândia',
  'Núcleo Bandeirante',
  'Sudoeste/Octogonal',
  'Jardim Botânico',
  'Lago Sul',
  'Lago Norte',
  'SIA',
  'Vicente Pires',
  'Arniqueira',
  'Fercal',
  'Outra'
];

const LeadFormModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    estado: '',
    cidade: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const isDistritoFederal = formData.estado === 'DF';

  // Atualizar URL quando modal abrir para tracking do Google Ads
  useEffect(() => {
    if (isOpen) {
      const currentPath = window.location.pathname;
      const newPath = `${currentPath}?lead-form=open`;
      navigate(newPath, { replace: true });
    }
  }, [isOpen, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      // Ao trocar o estado, limpar cidade se não for DF
      if (name === 'estado' && value !== 'DF') {
        next.cidade = '';
      }
      return next;
    });
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (name === 'estado' && errors.cidade) {
      setErrors(prev => ({ ...prev, cidade: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.telefone.trim()) {
      newErrors.telefone = 'Telefone é obrigatório';
    } else {
      const phoneRegex = /^[\d\s\(\)\-\+]{10,15}$/;
      if (!phoneRegex.test(formData.telefone.replace(/\s/g, ''))) {
        newErrors.telefone = 'Telefone inválido';
      }
    }

    if (!formData.estado) {
      newErrors.estado = 'Estado é obrigatório';
    }

    if (formData.estado === 'DF' && !formData.cidade) {
      newErrors.cidade = 'Cidade/Região é obrigatória no Distrito Federal';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simular salvamento (pode ser substituído por chamada de API)
    await new Promise(resolve => setTimeout(resolve, 1500));

    // Mostrar animação de sucesso
    setShowSuccess(true);

    // Aguardar animação antes de redirecionar
    setTimeout(() => {
      const companyPhone = '5562993263202';
      const estadoNome = ESTADOS_BRASIL.find(e => e.sigla === formData.estado)?.nome || formData.estado;
      const localizacao = formData.estado === 'DF' && formData.cidade
        ? `${formData.cidade}, ${estadoNome}`
        : estadoNome;
      const message = `Olá! Meu nome é ${formData.nome}, sou de ${localizacao} e gostaria de mais informações sobre os móveis artesanais.\n\nMeu telefone: ${formData.telefone}`;
      const whatsappUrl = `https://wa.me/${companyPhone}?text=${encodeURIComponent(message)}`;
      
      const currentPath = window.location.pathname;
      navigate(`${currentPath}?lead-form=converted`, { replace: true });
      
      window.open(whatsappUrl, '_blank');
      
      setTimeout(() => {
        onClose();
        setShowSuccess(false);
        setFormData({ nome: '', telefone: '', estado: '', cidade: '' });
        setIsSubmitting(false);
      }, 500);
    }, 2000);
  };

  const handleClose = () => {
    if (!isSubmitting && !showSuccess) {
      onClose();
      // Limpar parâmetro da URL ao fechar
      const currentPath = window.location.pathname;
      navigate(currentPath, { replace: true });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="">
      <div className="max-w-md mx-auto py-6">
        {!showSuccess ? (
          <>
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                <Icon name="UserPlus" size={32} className="text-primary" />
              </div>
              <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-2">
                Solicite seu Orçamento
              </h2>
              <p className="text-text-secondary">
                Preencha seus dados e entraremos em contato
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="text"
                name="nome"
                label="Nome Completo"
                placeholder="Digite seu nome completo"
                value={formData.nome}
                onChange={handleChange}
                error={errors.nome}
                required
                disabled={isSubmitting}
              />

              <Input
                type="tel"
                name="telefone"
                label="Telefone/WhatsApp"
                placeholder="(61) 99999-9999"
                value={formData.telefone}
                onChange={handleChange}
                error={errors.telefone}
                required
                disabled={isSubmitting}
              />

              <div className="space-y-2">
                <label className="text-sm font-medium leading-none text-foreground">
                  Estado <span className="text-destructive">*</span>
                </label>
                <select
                  name="estado"
                  value={formData.estado}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`flex h-10 w-full rounded-md border ${
                    errors.estado ? 'border-destructive' : 'border-input'
                  } bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  <option value="">Selecione seu estado</option>
                  {ESTADOS_BRASIL.map((estado) => (
                    <option key={estado.sigla} value={estado.sigla}>
                      {estado.nome}
                    </option>
                  ))}
                </select>
                {errors.estado && (
                  <p className="text-sm text-destructive">{errors.estado}</p>
                )}
              </div>

              {isDistritoFederal && (
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none text-foreground">
                    Cidade / Região (DF) <span className="text-destructive">*</span>
                  </label>
                  <select
                    name="cidade"
                    value={formData.cidade}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className={`flex h-10 w-full rounded-md border ${
                      errors.cidade ? 'border-destructive' : 'border-input'
                    } bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    <option value="">Selecione a cidade ou região</option>
                    {CIDADES_DISTRITO_FEDERAL.map((cidade) => (
                      <option key={cidade} value={cidade}>
                        {cidade}
                      </option>
                    ))}
                  </select>
                  {errors.cidade && (
                    <p className="text-sm text-destructive">{errors.cidade}</p>
                  )}
                </div>
              )}

              <div className="pt-4">
                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  fullWidth
                  disabled={isSubmitting}
                  className="bg-gradient-forest hover:shadow-forest"
                  iconName={isSubmitting ? "Loader2" : "Send"}
                  iconPosition="right"
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar Solicitação'}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-success/10 rounded-full mb-6 animate-bounce">
              <Icon name="CheckCircle" size={48} className="text-success" />
            </div>
            <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-2">
              Informações Salvas!
            </h2>
            <p className="text-text-secondary mb-4">
              Redirecionando para o WhatsApp...
            </p>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default LeadFormModal;
